import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import {
  closeDoc,
  KEYED_DOC_PATH,
  type OpenDoc,
  sameDoc,
  useOpenDocs,
} from '../../lib/open-docs'
import { useSpace } from '../../lib/org'
import { useTRPC } from '../../lib/trpc'
import { NavList, NavListItem, useNavListView } from '../../ui/NavList'
import { DOC_ICON, DocCover, type StudioDoc, sharedDoc } from './DocCover'
import './open-docs.css'

const names = (w: OpenDoc, n: StudioDoc) =>
  n.kind === w.kind && (w.kind === 'book' ? n.id === w.ref : n.key === w.ref)

/**
 * The documents a person has open, as a list or as thumbnails: a row each with
 * its kind and name, or each as its cover above its name. Either way every one
 * has a way to close it, and the one on screen is marked the way a nav row is.
 */
export function OpenDocs({
  current,
  currentTitle,
}: {
  current: OpenDoc | null
  /** The name the document on screen gives itself, which holds where the shelf
      does not list it — an archived page. */
  currentTitle?: ReactNode
}) {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const { spaces } = useSpace()
  const open = useOpenDocs()
  const [view, setView] = useNavListView('openDocsView')
  /* Every space Studio works in, not only the active one: switching to an
     org's shelf leaves the person's own documents open, and theirs too. */
  const own = useQueries({
    queries: spaces.map((s) =>
      trpc.documents.list.queryOptions({ spaceId: s.spaceId }),
    ),
  })
  /* A document shared with this person lives in somebody else's space. */
  const shared = useQuery(trpc.share.sharedWithMe.queryOptions())
  const known: StudioDoc[] = [
    ...own.flatMap((q) => q.data ?? []),
    ...(shared.data ?? []).map(sharedDoc),
  ]
  const settled =
    spaces.length > 0 && own.every((q) => q.isSuccess) && shared.isSuccess

  /* A document that was deleted, or whose share ended, is no longer anybody's to
     open, so it leaves the list once both answers are in. The one on screen
     stays: its own page says what became of it. */
  const gone = settled
    ? open.filter(
        (w) =>
          !(current && sameDoc(w, current)) && !known.some((n) => names(w, n)),
      )
    : []
  useEffect(() => {
    for (const w of gone) closeDoc(w)
  }, [gone])

  const close = (w: OpenDoc) => {
    if (current && sameDoc(w, current)) {
      const at = open.findIndex((o) => sameDoc(o, w))
      const next = open[at + 1] ?? open[at - 1]
      closeDoc(w)
      if (next) go(next)
      else navigate({ to: '/documents' })
      return
    }
    closeDoc(w)
  }
  const go = (w: OpenDoc) => {
    const search = w.spaceId ? { spaceId: w.spaceId } : {}
    if (w.kind === 'book') navigate({ to: '/books/$id', params: { id: w.ref } })
    else
      navigate({ to: KEYED_DOC_PATH[w.kind], params: { key: w.ref }, search })
  }

  if (open.length === 0)
    return <p className="open-docs__empty">Documents you open stay here.</p>

  return (
    <NavList label="Open documents" view={view} onViewChange={setView}>
      {open.map((w) => {
        const studioDoc = known.find((n) => names(w, n))
        const here = !!current && sameDoc(w, current)
        const title = studioDoc?.title ?? (here ? null : 'Untitled')
        const closeLabel = title ? `Close ${title}` : 'Close this document'
        const search = w.spaceId ? { spaceId: w.spaceId } : {}
        return (
          <NavListItem
            key={`${w.kind}:${w.ref}`}
            view={view}
            current={here}
            icon={DOC_ICON[w.kind]}
            name={title ?? currentTitle ?? 'Untitled'}
            picture={studioDoc ? <DocCover studioDoc={studioDoc} /> : null}
            render={(entry) =>
              w.kind === 'book' ? (
                <Link to="/books/$id" params={{ id: w.ref }} {...entry} />
              ) : (
                <Link
                  to={KEYED_DOC_PATH[w.kind]}
                  params={{ key: w.ref }}
                  search={search}
                  {...entry}
                />
              )
            }
            trailing={
              <button
                type="button"
                className="nav-list__action"
                aria-label={closeLabel}
                onClick={() => close(w)}
              >
                <X size={14} aria-hidden />
              </button>
            }
          />
        )
      })}
    </NavList>
  )
}
